import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Building2, Users } from 'lucide-react';
import { toast } from 'sonner';

const CUSTOMER_TYPES = [
  { value: 'partner', label: 'Master Distributor', desc: 'Top-tier — manages Sub Distributors and LCOs', icon: Users },
  { value: 'iptv_distributor', label: 'Sub Distributor', desc: 'Mid-tier — resells through their own LCOs', icon: Users },
  { value: 'lco', label: 'LCO', desc: 'Last-mile Local Cable Operator', icon: Users },
  { value: 'corporate', label: 'Enterprise', desc: 'Corporate customer for ILL / MPLS', icon: Building2 },
];

// Enterprise / LCO sales users may only create their own customer type
const allowedCustomerTypes = (user) => {
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const unrestricted = roles.some(r => !['enterprise_sales', 'lco_sales'].includes(r));
  if (unrestricted) return CUSTOMER_TYPES;
  const allowed = [];
  if (roles.includes('enterprise_sales')) allowed.push('corporate');
  if (roles.includes('lco_sales')) allowed.push('lco');
  return CUSTOMER_TYPES.filter(t => allowed.includes(t.value));
};

export default function CustomerForm() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const typeOptions = allowedCustomerTypes(user);
  const [formData, setFormData] = useState({
    customer_type: typeOptions[0].value,
    company_name: '',
    firm_type: 'private_limited',
    registered_address: '',
    city: '',
    state: '',
    pincode: '',
    contact_person: '',
    contact_designation: '',
    contact_email: '',
    contact_phone: '',
    territory: '',
    gst_number: '',
    pan_number: '',
    operator_share: '',
    isp_share: '',
    service_type: 'Internet Leased Line',
    billing_cycle: 'monthly',
    payment_terms: 'Net 30 days',
    contract_period: 12,
    uptime_sla: '99.5%',
    installation_charges: '',
    security_deposit: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        operator_share: formData.operator_share ? parseFloat(formData.operator_share) : null,
        isp_share: formData.isp_share ? parseFloat(formData.isp_share) : null,
        installation_charges: formData.installation_charges ? parseFloat(formData.installation_charges) : null,
        security_deposit: formData.security_deposit ? parseFloat(formData.security_deposit) : null,
      };
      const response = await axios.post(`${API}/customers`, payload);
      toast.success(`Customer created! ID: ${response.data.customer_code}`);
      navigate(`/customers/${response.data.customer_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const isPartner = formData.customer_type === 'partner';

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="customer-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/customers')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">New Customer</h2>
          <p className="text-slate-600 mt-1">Onboard a new IPTV Provider</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Customer Type Selection */}
        <Card className="mb-6">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle>Customer Type</CardTitle>
            <CardDescription>Select the type of customer</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {typeOptions.map((type) => {
                const Icon = type.icon;
                return (
                  <div
                    key={type.value}
                    data-testid={`customer-type-${type.value}`}
                    onClick={() => setFormData({ ...formData, customer_type: type.value })}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.customer_type === type.value
                        ? 'border-sky-500 bg-sky-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Icon className={`h-6 w-6 mb-2 ${formData.customer_type === type.value ? 'text-sky-600' : 'text-slate-400'}`} />
                    <p className="font-medium">{type.label}</p>
                    <p className="text-xs text-slate-500">{type.desc}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Company Details */}
        <Card className="mb-6">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  placeholder="Enter company name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="firm_type">Firm Type</Label>
                <Select 
                  value={formData.firm_type} 
                  onValueChange={(value) => setFormData({ ...formData, firm_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private_limited">Private Limited</SelectItem>
                    <SelectItem value="public_limited">Public Limited</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="proprietorship">Proprietorship</SelectItem>
                    <SelectItem value="llp">LLP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registered_address">Registered Address *</Label>
              <Textarea
                id="registered_address"
                value={formData.registered_address}
                onChange={(e) => setFormData({ ...formData, registered_address: e.target.value })}
                required
                rows={2}
                placeholder="Complete registered address"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pincode">Pincode *</Label>
                <Input
                  id="pincode"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gst_number">GST Number</Label>
                <Input
                  id="gst_number"
                  value={formData.gst_number}
                  onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pan_number">PAN Number</Label>
                <Input
                  id="pan_number"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                  placeholder="AAAAA0000A"
                />
              </div>
            </div>

            {isPartner && (
              <div className="space-y-2">
                <Label htmlFor="territory">Operating Territory *</Label>
                <Input
                  id="territory"
                  value={formData.territory}
                  onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
                  placeholder="e.g., Chennai Metro, Tamil Nadu"
                  required={isPartner}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Details */}
        <Card className="mb-6">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle>Contact Details</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person *</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  required
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_designation">Designation</Label>
                <Input
                  id="contact_designation"
                  value={formData.contact_designation}
                  onChange={(e) => setFormData({ ...formData, contact_designation: e.target.value })}
                  placeholder="e.g., Director, Manager"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  required
                  placeholder="email@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Phone *</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  required
                  placeholder="+91 XXXXXXXXXX"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Sharing (Partners Only) */}
        {isPartner && (
          <Card className="mb-6">
            <CardHeader className="border-b border-slate-100 bg-sky-50/50">
              <CardTitle className="text-sky-900">Revenue Sharing</CardTitle>
              <CardDescription>Configure revenue split between Operator and ISP</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="operator_share">Operator Share (%)</Label>
                  <Input
                    id="operator_share"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.operator_share}
                    onChange={(e) => {
                      const opShare = parseFloat(e.target.value) || 0;
                      setFormData({ 
                        ...formData, 
                        operator_share: e.target.value,
                        isp_share: (100 - opShare).toString()
                      });
                    }}
                    placeholder="e.g., 70"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="isp_share">ISP Share (%)</Label>
                  <Input
                    id="isp_share"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.isp_share}
                    onChange={(e) => setFormData({ ...formData, isp_share: e.target.value })}
                    placeholder="e.g., 30"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Commercial Terms (Non-Partners) */}
        {!isPartner && (
          <Card className="mb-6">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle>Commercial Terms</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service_type">Service Type</Label>
                  <Select 
                    value={formData.service_type} 
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Internet Leased Line">Internet Leased Line (ILL)</SelectItem>
                      <SelectItem value="Point to Point">Point to Point (P2P)</SelectItem>
                      <SelectItem value="MPLS VPN">MPLS VPN</SelectItem>
                      <SelectItem value="Broadband Premium">Broadband Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contract_period">Contract Period (months)</Label>
                  <Input
                    id="contract_period"
                    type="number"
                    value={formData.contract_period}
                    onChange={(e) => setFormData({ ...formData, contract_period: parseInt(e.target.value) || 12 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select 
                    value={formData.billing_cycle} 
                    onValueChange={(value) => setFormData({ ...formData, billing_cycle: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="half_yearly">Half Yearly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Select 
                    value={formData.payment_terms} 
                    onValueChange={(value) => setFormData({ ...formData, payment_terms: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Advance">Advance</SelectItem>
                      <SelectItem value="Net 15 days">Net 15 days</SelectItem>
                      <SelectItem value="Net 30 days">Net 30 days</SelectItem>
                      <SelectItem value="Net 45 days">Net 45 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Uptime SLA</Label>
                  <Select 
                    value={formData.uptime_sla} 
                    onValueChange={(value) => setFormData({ ...formData, uptime_sla: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="99.0%">99.0%</SelectItem>
                      <SelectItem value="99.5%">99.5%</SelectItem>
                      <SelectItem value="99.9%">99.9%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Installation Charges (₹)</Label>
                  <Input
                    type="number"
                    value={formData.installation_charges}
                    onChange={(e) => setFormData({ ...formData, installation_charges: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Security Deposit (₹)</Label>
                  <Input
                    type="number"
                    value={formData.security_deposit}
                    onChange={(e) => setFormData({ ...formData, security_deposit: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/customers')}>
            Cancel
          </Button>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={loading}>
            {loading ? 'Creating...' : 'Create Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
}
